import { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { ApiResponse } from 'lib-shared-types-api';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import {
  CreateBulletinApiArg,
  CreateBulletinApiResponse,
  CreateBulletinBoardApiArg,
  CreateBulletinBoardApiResponse,
  DeleteBulletinApiArg,
  DeleteBulletinBoardApiArg,
  DeleteBulletinBoardApiResponse,
  ReadBulletinApiArg,
  ReadBulletinApiResponse,
  ReadBulletinsApiArg,
  ReadBulletinsApiResponse,
  UpdateBulletinApiArg,
} from './types/bulletinboardsApi.types';
import {
  transformCreateBulletinBoardApiResponse,
  transformCreateBulletinResponse,
  transformReadBulletinResponse,
} from './utils/transformResponse';

export const bulletinboardsApi = api
  .enhanceEndpoints({
    addTagTypes: ['Bulletins'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      createBulletinBoard: builder.mutation<
        CreateBulletinBoardApiResponse,
        CreateBulletinBoardApiArg
      >({
        query: ({ organizationId }) => ({
          version: 1,
          path: 'bulletinboards',
          method: 'POST',
          body: { organization_id: organizationId },
        }),
        transformResponse: transformCreateBulletinBoardApiResponse,
      }),
      deleteBulletinBoard: builder.mutation<
        DeleteBulletinBoardApiResponse,
        DeleteBulletinBoardApiArg
      >({
        query: ({ boardId }) => ({
          version: 1,
          path: `bulletinboards/${boardId}`,
          method: 'DELETE',
        }),
      }),
      readBulletins: builder.query<
        ReadBulletinsApiResponse,
        ReadBulletinsApiArg
      >({
        query: ({ boardId }) => ({
          version: 1,
          path: `bulletinboards/${boardId}/bulletins`,
        }),
        providesTags: (readBulletinsResponse) => [
          ...(readBulletinsResponse?.data.bulletins || []).map(({ id }) => ({
            type: 'Bulletins' as const,
            id,
          })),
          { type: 'Bulletins', id: 'LIST' },
        ],
      }),
      createBulletin: builder.mutation<
        CreateBulletinApiResponse,
        CreateBulletinApiArg
      >({
        query: ({ boardId, title, order }) => ({
          version: 1,
          path: `bulletinboards/${boardId}/bulletins`,
          method: 'POST',
          body: { title, order },
        }),
        transformResponse: transformCreateBulletinResponse,
        invalidatesTags: [{ type: 'Bulletins', id: 'LIST' }],
      }),
      readBulletin: builder.query<ReadBulletinApiResponse, ReadBulletinApiArg>({
        query: ({ boardId, bulletinId }) => ({
          version: 1,
          path: `bulletinboards/${boardId}/bulletins/${bulletinId}`,
        }),
        transformResponse: transformReadBulletinResponse,
        providesTags: (readBulletinsResponse) => [
          {
            type: 'Bulletins',
            ...(readBulletinsResponse && {
              id: readBulletinsResponse.id,
            }),
          },
        ],
      }),
      deleteBulletin: builder.mutation<void, DeleteBulletinApiArg>({
        query: ({ boardId, bulletinId }) => ({
          version: 1,
          path: `bulletinboards/${boardId}/bulletins/${bulletinId}`,
          method: 'DELETE',
        }),
        async onQueryStarted(
          { boardId, bulletinId },
          { dispatch, queryFulfilled },
        ) {
          const patchReadBulletins = dispatch(
            bulletinboardsApi.util.updateQueryData(
              'readBulletins',
              { boardId },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<ReadBulletinsApiResponse>
                >;
                mutableDraft.data.bulletins = draft.data.bulletins.filter(
                  (bulletin) => bulletin.id !== bulletinId,
                );
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchReadBulletins.undo();
          }
        },
      }),
      updateBulletin: builder.mutation<ApiResponse, UpdateBulletinApiArg>({
        query: ({ boardId, bulletinId, update: bulletinUpdate }) => ({
          version: 1,
          path: `bulletinboards/${boardId}/bulletins/${bulletinId}`,
          method: 'PATCH',
          body: bulletinUpdate,
        }),
        async onQueryStarted(
          { boardId, bulletinId, update: bulletinUpdate },
          { dispatch, queryFulfilled },
        ) {
          const patchReadBulletins = dispatch(
            bulletinboardsApi.util.updateQueryData(
              'readBulletins',
              { boardId },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<ReadBulletinsApiResponse>
                >;
                mutableDraft.data.bulletins = mutableDraft.data.bulletins
                  .map((bulletin) =>
                    bulletin.id === bulletinId
                      ? { ...bulletin, ...bulletinUpdate }
                      : bulletin,
                  )
                  .sort(
                    (bulletin1, bulletin2) => bulletin1.order - bulletin2.order,
                  );
              },
            ),
          );
          const patchReadBulletin = dispatch(
            bulletinboardsApi.util.updateQueryData(
              'readBulletin',
              { boardId, bulletinId },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<ReadBulletinApiResponse>
                >;
                for (const [key, value] of Object.entries(bulletinUpdate)) {
                  mutableDraft[key] = value;
                }
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchReadBulletins.undo();
            patchReadBulletin.undo();
          }
        },
      }),
    }),
  });
